import React, { useState, useEffect } from 'react';

const TestimonialModal = ({ isOpen, onClose }) => {
    const [status, setStatus] = useState({ loading: false, message: '', type: '' });
    
    // Default User Type 'school' rakha hai taki modal khulte hi blank na dikhe
    const [formData, setFormData] = useState({ 
        userType: 'school', 
        name: '', 
        designation: '', 
        schoolName: '', 
        city: '', 
        message: '' 
    });
    
    const [profilePic, setProfilePic] = useState(null);
    const [isAnimatingOut, setIsAnimatingOut] = useState(false);

    // --- LOGIC: Auto Capitalize First Letter ---
    const toTitleCase = (str) => {
        return str.replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // Agar Name, City ya School Name hai to Auto-Capitalize karein
        if (['name', 'schoolName', 'city', 'designation'].includes(name)) {
            setFormData(prev => ({ ...prev, [name]: toTitleCase(value) }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setProfilePic(e.target.files[0]);
        }
    };

    // --- LOGIC: Reset & Close Animation ---
    const resetForm = () => {
        setFormData({ userType: 'school', name: '', designation: '', schoolName: '', city: '', message: '' });
        setProfilePic(null);
        setStatus({ loading: false, message: '', type: '' });
    };

    const handleClose = () => {
        setIsAnimatingOut(true);
        setTimeout(() => {
            onClose();
            resetForm();
            setIsAnimatingOut(false);
        }, 300);
    };

    // Close on Escape Key
    useEffect(() => {
        const handleKeyDown = (event) => { if (event.key === 'Escape') handleClose(); };
        if (isOpen) window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen]);

    // --- LOGIC: Form Submission to Server ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!profilePic) {
            setStatus({ loading: false, message: 'Please upload a profile picture.', type: 'error' });
            return;
        }

        setStatus({ loading: true, message: 'Submitting feedback...', type: 'info' });
        
        const data = new FormData();
        Object.keys(formData).forEach(key => data.append(key, formData[key]));
        data.append('profile_pic', profilePic);

        try {
            // Ye API call aapke server.js wale naye route par jayega
            const response = await fetch('/api/feedback/submit', { method: 'POST', body: data });
            const result = await response.json();
            
            if (result.success) {
                setStatus({ loading: false, message: 'Thank you! Submitted successfully.', type: 'success' });
                setTimeout(handleClose, 2000);
            } else {
                setStatus({ loading: false, message: result.message || 'Submission failed.', type: 'error' });
            }
        } catch (error) {
            console.error(error);
            setStatus({ loading: false, message: 'Server error. Try again.', type: 'error' });
        }
    };

    if (!isOpen) return null;

    // --- STYLES: Matching your screenshot ---
    // Dark Input Style
    const inputClass = "w-full bg-[#1e293b] border border-slate-700 rounded-2xl px-5 py-4 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500/50 transition-all text-sm font-medium";
    // Label Style
    const labelClass = "block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 ml-2";

    return (
        <div 
            className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-[999] p-4"
            onClick={handleClose}
        >
            <div 
                className={`bg-[#0f172a] w-full max-w-lg rounded-3xl relative shadow-2xl border border-white/10 overflow-hidden
                    ${isAnimatingOut ? 'animate-out fade-out zoom-out-95 duration-300' : 'animate-in fade-in zoom-in-95 duration-300'}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close Button */}
                <button onClick={handleClose} className="absolute top-5 right-5 text-slate-500 hover:text-white transition-colors">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                </button>

                <div className="p-8">
                    <h2 className="text-2xl font-bold text-white text-center mb-8">Share Your Experience</h2>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        
                        {/* 1. I AM A... (Toggle) */}
                        <div>
                            <label className={labelClass}>I am a...</label>
                            <div className="grid grid-cols-2 gap-3 p-1 bg-[#1e293b] rounded-full border border-slate-700">
                                <button 
                                    type="button" 
                                    onClick={() => setFormData({...formData, userType: 'school'})}
                                    className={`py-3 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${formData.userType === 'school' ? 'bg-orange-500 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
                                >
                                    School
                                </button>
                                <button 
                                    type="button" 
                                    onClick={() => setFormData({...formData, userType: 'job_seeker'})}
                                    className={`py-3 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${formData.userType === 'job_seeker' ? 'bg-orange-500 text-white shadow-lg' : 'text-slate-400 hover:text-white'}`}
                                >
                                    Job Seeker
                                </button>
                            </div>
                        </div>

                        {/* 2. Name & Designation (Side by Side) */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className={labelClass}>Your Name</label>
                                <input 
                                    type="text" 
                                    name="name" 
                                    value={formData.name} 
                                    onChange={handleChange} 
                                    placeholder="e.g. Anjali Sharma" 
                                    className={inputClass} 
                                    required 
                                />
                            </div>
                            <div>
                                <label className={labelClass}>Your Designation</label>
                                <input 
                                    type="text" 
                                    name="designation" 
                                    value={formData.designation} 
                                    onChange={handleChange} 
                                    placeholder="e.g. Principal" 
                                    className={inputClass} 
                                    required 
                                />
                            </div>
                        </div>

                        {/* 3. School Name OR City (Dynamic Field) */}
                        <div className="animate-in fade-in">
                            {formData.userType === 'school' ? (
                                <div>
                                    <label className={labelClass}>School Name</label>
                                    <input 
                                        type="text" 
                                        name="schoolName" 
                                        value={formData.schoolName} 
                                        onChange={handleChange} 
                                        placeholder="e.g. St. Xavier's School" 
                                        className={inputClass} 
                                        required 
                                    />
                                </div>
                            ) : (
                                <div>
                                    <label className={labelClass}>City</label>
                                    <input 
                                        type="text" 
                                        name="city" 
                                        value={formData.city} 
                                        onChange={handleChange} 
                                        placeholder="e.g. Jaipur" 
                                        className={inputClass} 
                                        required 
                                    />
                                </div>
                            )}
                        </div>

                        {/* 4. Message */}
                        <div>
                            <label className={labelClass}>Your Message</label>
                            <textarea 
                                name="message" 
                                value={formData.message} 
                                onChange={handleChange} 
                                rows="3" 
                                placeholder="Share your experience with us..." 
                                className={`${inputClass} resize-none`} 
                                required
                            ></textarea>
                        </div>

                        {/* 5. Profile Picture */}
                        <div>
                            <label className={labelClass}>Profile Picture</label>
                            <label className={`${inputClass} flex items-center justify-center cursor-pointer hover:bg-slate-800 group`}>
                                <div className="flex flex-col items-center">
                                    <span className="text-slate-400 group-hover:text-white transition-colors truncate max-w-[200px]">
                                        {profilePic ? `Selected: ${profilePic.name}` : "Click to upload an image"}
                                    </span>
                                </div>
                                <input type="file" accept="image/*" className="hidden" onChange={handleFileChange} required />
                            </label>
                        </div>

                        {/* Status Message */}
                        {status.message && (
                            <div className={`text-center text-xs font-bold py-2 px-4 rounded-lg ${status.type === 'error' ? 'bg-red-500/10 text-red-400' : 'bg-green-500/10 text-green-400'}`}>
                                {status.message}
                            </div>
                        )}

                        {/* 6. Submit Button */}
                        <button 
                            type="submit" 
                            disabled={status.loading}
                            className="w-full bg-orange-500 hover:bg-orange-600 text-white py-4 rounded-full font-black uppercase tracking-[0.15em] text-xs shadow-lg shadow-orange-500/25 transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                        >
                            {status.loading ? 'Submitting...' : 'Submit Feedback'}
                        </button>

                    </form>
                </div>
            </div>
        </div>
    );
};

export default TestimonialModal;